/**
 * SC Chart Finder — SailorShop.co.uk
 * Admiralty Small Craft — folio-centric browsing
 * (c) SailorShop.co.uk — chart coverage data is proprietary.
 */
;(function () {
  'use strict';

  var DATA_URL = window.__scChartFinderDataUrl;
  if (!DATA_URL) { console.error('SC Chart Finder: no data URL'); return; }

  var MAP_CENTER = [54.0, -4.0], MAP_ZOOM = window.innerWidth < 769 ? 5 : 6;
  // CARTO stamps an "API KEY REQUIRED" watermark across raster tiles served
  // without a key. The key is set by snippets/carto-basemap-key.liquid — see
  // there for why it is public and where to rotate it.
  var CARTO_KEY = window.__cartoBasemapKey || '';
  var TILE_URL = 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png'
                 + (CARTO_KEY ? '?key=' + encodeURIComponent(CARTO_KEY) : '');
  var TILE_ATTR = '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/">CARTO</a>';
  var SC_COLOR = '#c03221';
  var SC_COLOR_FOLIO = '#e07b2a';

  // Discount tiers: [minQty, discountPercent]
  var DISCOUNT_TIERS = [[15, 75], [10, 65], [5, 35]];

  var CHART_DATA = null, map = null;
  var folioEntries = [], chartEntries = [], entriesById = new Map();
  var folioPolygons = new Map(), chartPolygons = new Map();
  var folioLabels = [];
  var activeEntry = null, activePolygon = null;
  var hoveredPoly = null, hoveredEntry = null;
  var currentFolio = null; // null = overview, string = drilled into a folio
  var viewMode = 'folios'; // 'charts' or 'folios'
  var activeFolio = null; // folio entry shown in bar after drill-in
  var ALL_TYPES = ['Passage', 'Coastal', 'Approach', 'Harbour'];
  var typeVisible = {};
  ALL_TYPES.forEach(function(t){ typeVisible[t] = true; });
  var cartVariantIds = new Set();
  var isMobile = window.innerWidth < 769;
  var searchIndex = [];

  var $map, $bar, $modal, $toast, $tooltip;
  var $legend, $legendToggle, $legendInfo, $breadcrumb, $backBtn, $folioLabel;
  var $modeCharts, $modeFolios;
  var $typeSection, $typeFilterRows;
  var $searchInput, $searchClear, $searchDropdown;

  document.addEventListener('DOMContentLoaded', init);

  function init() {
    $map            = document.getElementById('sc-map');
    $bar            = document.getElementById('sc-bar');
    $modal          = document.getElementById('sc-modal-backdrop');
    $toast          = document.getElementById('sc-toast');
    $tooltip        = document.getElementById('sc-tooltip');
    $legend         = document.getElementById('sc-legend');
    $legendToggle   = document.getElementById('sc-legend-toggle');
    $legendInfo     = document.getElementById('sc-legend-info');
    $breadcrumb     = document.getElementById('sc-breadcrumb');
    $backBtn        = document.getElementById('sc-back-btn');
    $folioLabel     = document.getElementById('sc-folio-label');
    $modeCharts     = document.getElementById('sc-mode-charts');
    $modeFolios     = document.getElementById('sc-mode-folios');
    $typeSection    = document.getElementById('sc-type-section');
    $typeFilterRows = document.getElementById('sc-type-filter-rows');
    $searchInput    = document.getElementById('sc-search-input');
    $searchClear    = document.getElementById('sc-search-clear');
    $searchDropdown = document.getElementById('sc-search-dropdown');
    if (!$map) return;

    initMap();
    fetchData();
    initSearch();
    initBar();
    initModal();
    initNav();
    initModeToggle();
    window.addEventListener('resize', function(){ isMobile = window.innerWidth < 769; if(map) map.invalidateSize(); });
    fetchCartState();
  }

  function initMap() {
    map = L.map($map, { center: MAP_CENTER, zoom: MAP_ZOOM, zoomControl: true, scrollWheelZoom: true });
    L.tileLayer(TILE_URL, { attribution: TILE_ATTR, maxZoom: 18, subdomains: 'abcd' }).addTo(map);
    map.on('click', function(e) { selectEntryFromPoint(e.latlng.lat, e.latlng.lng); });
  }

  /* ── Point-in-polygon ── */
  function pointInRing(lat, lng, ring) {
    var inside = false;
    for (var i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      var yi = ring[i][0], xi = ring[i][1], yj = ring[j][0], xj = ring[j][1];
      if (((yi > lat) !== (yj > lat)) && (lng < (xj - xi) * (lat - yi) / (yj - yi) + xi)) inside = !inside;
    }
    return inside;
  }
  function pointInCoords(lat, lng, coords) {
    if (!coords || !coords.length) return false;
    if (Array.isArray(coords[0][0])) return coords.some(function(r) { return pointInRing(lat, lng, r); });
    return pointInRing(lat, lng, coords);
  }
  function selectEntryFromPoint(lat, lng) {
    // Only PIP in chart views (not folio overview)
    if (viewMode === 'folios' && !currentFolio) { closeBar(); return; }
    var hits = [];
    chartPolygons.forEach(function(poly, id) {
      var entry = entriesById.get(id);
      if (!entry || !isEntryVisible(entry)) return;
      var el = poly.getElement ? poly.getElement() : null;
      if (el && el.style.display === 'none') return;
      if (pointInCoords(lat, lng, entry.coordinates)) hits.push({ entry: entry, poly: poly, area: effectiveArea(entry) });
    });
    if (!hits.length) { closeBar(); return; }
    hits.sort(function(a, b) { return a.area - b.area; });
    selectChart(hits[0].entry, hits[0].poly);
  }

  /* ── Data ── */
  function fetchData() {
    fetch(DATA_URL).then(function(r){ if(!r.ok) throw new Error('HTTP '+r.status); return r.json(); })
    .then(function(data) {
      CHART_DATA = data;
      chartEntries = data.charts || [];
      folioEntries = data.folios || [];
      chartEntries.forEach(function(e){ entriesById.set(e.id, e); });
      buildSearchIndex();
      buildTypeFilter();
      drawFolioView();

      var $loading = document.getElementById('sc-loading');
      if($loading) $loading.classList.add('hidden');
      setTimeout(function(){ map.invalidateSize(); }, 100);
    })
    .catch(function(err) {
      console.error('SC Chart Finder: data load failed', err);
      var $l = document.getElementById('sc-loading');
      if($l) $l.classList.add('hidden');
      var $e = document.getElementById('sc-error');
      if($e) $e.style.display = 'block';
    });
  }

  /* ── Mode toggle ── */
  function initModeToggle() {
    if ($modeCharts) $modeCharts.addEventListener('click', function(){ setMode('charts'); });
    if ($modeFolios) $modeFolios.addEventListener('click', function(){ setMode('folios'); });
  }

  function setMode(mode) {
    if (mode === viewMode && !currentFolio) return;
    viewMode = mode;
    currentFolio = null;
    activeFolio = null;
    if ($modeCharts) $modeCharts.classList.toggle('active', mode === 'charts');
    if ($modeFolios) $modeFolios.classList.toggle('active', mode === 'folios');
    updateNavUI();
    if (mode === 'charts') drawAllCharts();
    else drawFolioView();
  }

  /* ── Type filter ── */
  function buildTypeFilter() {
    if (!$typeFilterRows) return;
    $typeFilterRows.innerHTML = '';
    var counts = {};
    chartEntries.forEach(function(e){
      (e.types || []).forEach(function(t){ counts[t] = (counts[t] || 0) + 1; });
    });
    ALL_TYPES.forEach(function(t) {
      if (!counts[t]) return;
      var row = document.createElement('div');
      row.className = 'sc-type-filter-row';
      row.dataset.type = t;
      row.setAttribute('role', 'checkbox');
      row.setAttribute('aria-checked', 'true');
      row.tabIndex = 0;
      row.innerHTML = '<div class="sc-type-check">\u2713</div>' +
        '<span class="sc-type-label">' + esc(t) + '</span>' +
        '<span class="sc-type-count">' + counts[t] + '</span>';
      row.addEventListener('click', function(){ toggleType(t); });
      row.addEventListener('keydown', function(e){ if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); toggleType(t); } });
      $typeFilterRows.appendChild(row);
    });
  }

  function toggleType(t) {
    typeVisible[t] = !typeVisible[t];
    if ($typeFilterRows) {
      $typeFilterRows.querySelectorAll('.sc-type-filter-row[data-type="' + t + '"]').forEach(function(r){
        r.classList.toggle('dimmed', !typeVisible[t]);
        r.setAttribute('aria-checked', typeVisible[t] ? 'true' : 'false');
      });
    }
    applyVisibility();
  }

  function isEntryVisible(entry) {
    var types = entry.types || [];
    if (!types.length) return true;
    return types.some(function(t){ return typeVisible[t]; });
  }

  function applyVisibility() {
    chartPolygons.forEach(function(poly, id) {
      var entry = entriesById.get(id);
      if (!entry) return;
      var vis = isEntryVisible(entry);
      var el = poly.getElement ? poly.getElement() : null;
      if (el) { el.style.display = vis ? '' : 'none'; }
    });
  }

  /* ── Navigation ── */
  function initNav() {
    function goBackToFolios() {
      currentFolio = null;
      activeFolio = null;
      drawFolioView();
      updateNavUI();
      map.setView(MAP_CENTER, MAP_ZOOM, { animate: false });
    }
    if ($backBtn) $backBtn.addEventListener('click', goBackToFolios);
    var $mobileBack = document.getElementById('sc-mobile-back');
    if ($mobileBack) $mobileBack.addEventListener('click', goBackToFolios);
    if ($legendToggle) $legendToggle.addEventListener('click', function(){
      if ($legend) $legend.classList.add('open');
    });
    // Tap outside legend to close
    document.addEventListener('click', function(e){
      if (!$legend || !$legend.classList.contains('open')) return;
      if ($legend.contains(e.target)) return;
      if ($legendToggle && $legendToggle.contains(e.target)) return;
      $legend.classList.remove('open');
    });
  }

  /* ================================================================
     ALL CHARTS VIEW — show all 416 individual chart polygons
     ================================================================ */
  function drawAllCharts() {
    clearAllLayers();
    currentFolio = null;
    closeBar();
    updateNavUI();

    var sorted = chartEntries.slice().sort(function(a,b){ return effectiveArea(b) - effectiveArea(a); });
    sorted.forEach(function(entry) {
      var coords = entry.coordinates;
      if (!coords || !coords.length) return;
      var poly = L.polygon(coords, chartStyle(entry, 'default')).addTo(map);
      bindChartEvents(entry, poly);
      chartPolygons.set(entry.id, poly);
      updatePolyCartHighlight(entry);
    });

    $legendInfo.textContent = chartEntries.length + ' individual charts';
    applyVisibility();
  }

  /* ================================================================
     FOLIO VIEW — show folio bounding boxes
     ================================================================ */
  function drawFolioView() {
    clearAllLayers();
    currentFolio = null;
    activeFolio = null;
    closeBar();
    updateNavUI();

    // Sort largest area first so smallest folios render on top
    var sorted = folioEntries.slice().sort(function(a, b) {
      var ab = a.bounds, bb = b.bounds;
      var areaA = (ab[2] - ab[0]) * (ab[3] - ab[1]);
      var areaB = (bb[2] - bb[0]) * (bb[3] - bb[1]);
      return areaB - areaA;
    });
    sorted.forEach(function(f) {
      var b = f.bounds; // [s, w, n, e]
      var rect = L.rectangle([[b[0], b[1]], [b[2], b[3]]], {
        color: SC_COLOR_FOLIO, weight: 2, opacity: 0.7,
        fillColor: SC_COLOR_FOLIO, fillOpacity: 0.08,
      }).addTo(map);

      rect.on('mouseover', function(e){
        rect.setStyle({ fillOpacity: 0.2, weight: 3 });
        showFolioTooltip(e.originalEvent, f);
      });
      rect.on('mousemove', function(e){ moveTooltip(e.originalEvent); });
      rect.on('mouseout', function(){
        rect.setStyle({ fillOpacity: 0.08, weight: 2 });
        hideTooltip();
      });
      rect.on('click', function(e){
        L.DomEvent.stopPropagation(e);
        drillIntoFolio(f.folio);
      });

      folioPolygons.set(f.folio, rect);
    });

    $legendInfo.textContent = folioEntries.length + ' folio areas. Click any area to explore.';
  }

  /* ── Shared chart polygon event binding ── */
  function bindChartEvents(entry, poly) {
    poly.on('mouseover', function(e){
      hoveredPoly = poly; hoveredEntry = entry;
      // Use CSS class instead of setStyle to avoid SVG reflow flicker
      var el = poly.getElement ? poly.getElement() : null;
      if (el && entry !== activeEntry) el.classList.add('sc-poly-hover');
      showChartTooltip(e.originalEvent, entry);
    });
    poly.on('mousemove', function(e){ moveTooltip(e.originalEvent); });
    poly.on('mouseout', function(){
      var el = poly.getElement ? poly.getElement() : null;
      if (el) el.classList.remove('sc-poly-hover');
      hoveredPoly = null; hoveredEntry = null;
      hideTooltip();
    });
    poly.on('click', function(e){
      L.DomEvent.stopPropagation(e);
      selectChart(entry, poly);
    });
  }

  /* ================================================================
     CHART VIEW — drill into a folio
     ================================================================ */
  function drillIntoFolio(folioNum) {
    clearAllLayers();
    currentFolio = folioNum;
    closeBar();
    updateNavUI();

    var folioCharts = chartEntries.filter(function(c){ return c.folio === folioNum; });
    folioCharts.sort(function(a,b){ return effectiveArea(b) - effectiveArea(a); });

    // Fit map to folio bounds FIRST, then draw polygons after zoom settles
    var folio = folioEntries.find(function(f){ return f.folio === folioNum; });
    if (folio) {
      var b = folio.bounds;
      map.fitBounds([[b[0], b[1]], [b[2], b[3]]], { padding: [30, 30], animate: false });
    }

    folioCharts.forEach(function(entry) {
      var coords = entry.coordinates;
      if (!coords || !coords.length) return;
      var poly = L.polygon(coords, chartStyle(entry, 'default')).addTo(map);
      bindChartEvents(entry, poly);
      chartPolygons.set(entry.id, poly);
      updatePolyCartHighlight(entry);
    });

    applyVisibility();

    $legendInfo.textContent = folioCharts.length + ' charts in this area';

    // Show folio bar
    if (folio) {
      activeFolio = folio;
      showFolioBar(folio);
    }
  }

  function selectChart(entry, poly) {
    // Deselect previous
    if (activePolygon) {
      var prevEl = activePolygon.getElement ? activePolygon.getElement() : null;
      if (prevEl) prevEl.classList.remove('sc-poly-active');
    }
    activeEntry = entry;
    activePolygon = poly;
    activeFolio = null; // chart bar replaces folio bar
    if (poly) {
      var el = poly.getElement ? poly.getElement() : null;
      if (el) { el.classList.remove('sc-poly-hover'); el.classList.add('sc-poly-active'); }
    }
    showBar(entry);
  }

  /* ── Styles ── */
  function chartStyle(entry, state) {
    // Keep weight constant across default/hover to avoid SVG reflow flicker in dense areas
    var styles = {
      'default': { color: SC_COLOR, weight: 1.5, opacity: 0.6, fillColor: SC_COLOR, fillOpacity: 0.08 },
      hover:     { color: SC_COLOR, weight: 1.5, opacity: 1,   fillColor: SC_COLOR, fillOpacity: 0.22 },
      active:    { color: SC_COLOR, weight: 2.5, opacity: 1,   fillColor: SC_COLOR, fillOpacity: 0.28 },
    };
    return styles[state] || styles['default'];
  }

  function effectiveArea(entry) {
    var coords = entry.coordinates;
    if (!coords || !coords.length) return 0;
    if (Array.isArray(coords[0][0])) {
      var total = 0;
      coords.forEach(function(ring){ total += ringArea(ring); });
      return total;
    }
    return ringArea(coords);
  }

  function ringArea(pts) {
    var a = 0;
    for (var i = 0, j = pts.length - 1; i < pts.length; j = i++) {
      a += (pts[j][1] + pts[i][1]) * (pts[j][0] - pts[i][0]);
    }
    return Math.abs(a / 2);
  }

  /* ── Layer management ── */
  function clearAllLayers() {
    folioPolygons.forEach(function(p){ map.removeLayer(p); });
    folioPolygons.clear();
    chartPolygons.forEach(function(p){ map.removeLayer(p); });
    chartPolygons.clear();
    folioLabels.forEach(function(m){ map.removeLayer(m); });
    folioLabels = [];
    activeEntry = null; activePolygon = null;
    hoveredPoly = null; hoveredEntry = null;
  }

  function updateNavUI() {
    var drilled = viewMode === 'folios' && currentFolio;
    $breadcrumb.style.display = drilled ? '' : 'none';
    if (drilled) $folioLabel.textContent = currentFolio;
    // Show type filter in individual charts mode only (not folio drill-down)
    var showTypes = viewMode === 'charts';
    if ($typeSection) $typeSection.style.display = showTypes ? '' : 'none';
    // Mobile floating back button
    var $mobileBack = document.getElementById('sc-mobile-back');
    if ($mobileBack) {
      $mobileBack.style.display = (drilled && isMobile) ? '' : 'none';
    }
  }

  /* ================================================================
     BOTTOM BAR
     ================================================================ */
  function initBar() {
    if (!$bar) return;
    $bar.querySelector('.sc-bar-close').addEventListener('click', function(){
      activeFolio = null; // explicit close — don't restore folio bar
      closeBar();
    });
    // Details + thumb clicks are wired dynamically in showBar / showFolioBar
  }

  function fmtPrice(p) {
    var n = parseFloat(p);
    if (isNaN(n)) return '';
    return '\u00a3' + n.toFixed(2);
  }

  function showBar(entry) {
    if (!$bar) return;
    var prods = entry.products || [];
    // Filter to individual chart products (not folio sets)
    var chartProds = prods.filter(function(p) { return !p.isFolioSet; });
    var firstP = chartProds[0] || prods[0] || {};

    var thumb = $bar.querySelector('.sc-bar-thumb');
    thumb.src = firstP.chartImage || firstP.coverImage || '';
    thumb.alt = entry.chartRef;

    $bar.querySelector('.sc-bar-ref').textContent = entry.chartRef;
    $bar.querySelector('.sc-bar-title').textContent = entry.shortTitle;

    var types = (entry.types || []).join(', ');
    $bar.querySelector('.sc-bar-detail').textContent = types;

    $bar.querySelector('.sc-bar-price').textContent = firstP.price ? fmtPrice(firstP.price) : '';

    var cartBtn = $bar.querySelector('.sc-bar-btn-cart');
    var vid = firstP.variantId || 0;
    var inCart = cartVariantIds.has(vid);
    cartBtn.dataset.variantId = vid;
    cartBtn.dataset.entryId = entry.id;
    cartBtn.textContent = inCart ? 'In cart \u2713' : 'Add to cart';
    cartBtn.className = 'sc-bar-btn sc-bar-btn-cart' + (inCart ? ' in-cart' : '');
    cartBtn.disabled = false;
    cartBtn.onclick = inCart
      ? function(){ removeFromCart(vid, entry.id, cartBtn, 'bar'); }
      : function(){ addToCart(vid, entry.id, cartBtn, 'bar'); };

    var detailsBtn = $bar.querySelector('.sc-bar-btn-details');
    detailsBtn.textContent = 'Details';
    detailsBtn.onclick = function(){ if (activeEntry) openModal(activeEntry); };

    $bar.querySelector('.sc-bar-thumb').onclick = function(){ if (activeEntry) openModal(activeEntry); };

    $bar.classList.add('open');
  }

  function showFolioBar(folio) {
    if (!$bar || !folio) return;
    var fp = folio.product || {};
    var chartCount = folio.chartCount || 0;
    var singlePrice = 17.80;
    var fullPrice = chartCount * singlePrice;

    // Work out discount tier
    var discount = 0;
    for (var i = 0; i < DISCOUNT_TIERS.length; i++) {
      if (chartCount >= DISCOUNT_TIERS[i][0]) { discount = DISCOUNT_TIERS[i][1]; break; }
    }
    var discountedTotal = fullPrice * (1 - discount / 100);
    var folioPrice = parseFloat(fp.price) || discountedTotal;
    var saving = fullPrice - folioPrice;

    var thumb = $bar.querySelector('.sc-bar-thumb');
    thumb.src = fp.coverImage || '';
    thumb.alt = 'Folio ' + folio.folio;

    $bar.querySelector('.sc-bar-ref').textContent = folio.folio + ': ' + (folio.title || '');
    $bar.querySelector('.sc-bar-title').textContent = '';
    $bar.querySelector('.sc-bar-detail').textContent = chartCount + ' charts, save ' + fmtPrice(saving);

    $bar.querySelector('.sc-bar-price').textContent = fmtPrice(fp.price);

    var cartBtn = $bar.querySelector('.sc-bar-btn-cart');
    var vid = fp.variantId || 0;
    var inCart = cartVariantIds.has(vid);
    cartBtn.dataset.variantId = vid;
    cartBtn.dataset.entryId = '';
    cartBtn.textContent = inCart ? 'In cart \u2713' : 'Add folio to cart';
    cartBtn.className = 'sc-bar-btn sc-bar-btn-cart' + (inCart ? ' in-cart' : '');
    cartBtn.disabled = !vid;
    cartBtn.onclick = inCart
      ? function(){ removeFromCart(vid, '', cartBtn, 'folio'); }
      : function(){ addToCart(vid, '', cartBtn, 'folio'); };

    var detailsBtn = $bar.querySelector('.sc-bar-btn-details');
    detailsBtn.textContent = 'View folio page';
    detailsBtn.onclick = function(){ if (fp.handle) window.open('/products/' + fp.handle, '_blank'); };

    $bar.querySelector('.sc-bar-thumb').onclick = function(){ if (fp.handle) window.open('/products/' + fp.handle, '_blank'); };

    $bar.classList.add('open');
  }

  function closeBar() {
    if ($bar) $bar.classList.remove('open');
    if (activePolygon) {
      var el = activePolygon.getElement ? activePolygon.getElement() : null;
      if (el) el.classList.remove('sc-poly-active');
    }
    activeEntry = null; activePolygon = null;
    // If we're drilled into a folio and closed a chart bar, restore folio bar
    if (currentFolio && activeFolio) {
      setTimeout(function(){ showFolioBar(activeFolio); }, 50);
    }
  }

  /* ================================================================
     MODAL
     ================================================================ */
  function initModal() {
    if (!$modal) return;
    $modal.addEventListener('click', function(e){ if (e.target === $modal) closeModal(); });
    $modal.querySelector('.sc-modal-close').addEventListener('click', closeModal);

    var imgToggle = $modal.querySelector('.sc-modal-img-toggle');
    var img = $modal.querySelector('.sc-modal-img');
    if (imgToggle) {
      imgToggle.addEventListener('click', function(){
        if (imgToggle.dataset.mode === 'chart') {
          img.src = imgToggle.dataset.cover; imgToggle.textContent = 'Show chart'; imgToggle.dataset.mode = 'cover';
        } else {
          img.src = imgToggle.dataset.chart; imgToggle.textContent = 'Show cover'; imgToggle.dataset.mode = 'chart';
        }
        img.classList.remove('zoomed');
      });
    }
    if (img) {
      var fsOverlay = document.createElement('div');
      fsOverlay.className = 'sc-img-fullscreen';
      fsOverlay.innerHTML = '<img src="" alt=""/><div class="sc-img-fullscreen-hint">Tap to close</div>';
      document.body.appendChild(fsOverlay);
      fsOverlay.addEventListener('click', function(){ fsOverlay.classList.remove('open'); });

      img.addEventListener('click', function(){
        if (isMobile) {
          fsOverlay.querySelector('img').src = img.src;
          fsOverlay.classList.add('open');
        } else {
          img.classList.toggle('zoomed');
        }
      });
      img.addEventListener('mousemove', function(e){
        if (!img.classList.contains('zoomed')) return;
        var rect = img.getBoundingClientRect();
        var x = ((e.clientX - rect.left) / rect.width) * 100;
        var y = ((e.clientY - rect.top) / rect.height) * 100;
        img.style.transformOrigin = x + '% ' + y + '%';
      });
      img.addEventListener('mouseleave', function(){
        img.classList.remove('zoomed');
        img.style.transformOrigin = '';
      });
    }
    document.addEventListener('keydown', function(e){ if (e.key === 'Escape' && $modal.classList.contains('open')) closeModal(); });
  }

  function openModal(entry) {
    if (!$modal) return;
    var prods = entry.products || [], firstP = prods[0] || {};

    var coverUrl = firstP.coverImage || '', chartUrl = firstP.chartImage || '';
    var img = $modal.querySelector('.sc-modal-img');
    img.src = chartUrl || coverUrl || ''; img.alt = entry.title;
    img.classList.remove('zoomed'); img.style.transformOrigin = '';

    var imgToggle = $modal.querySelector('.sc-modal-img-toggle');
    if (coverUrl && chartUrl) {
      imgToggle.style.display = ''; imgToggle.textContent = 'Show cover';
      imgToggle.dataset.mode = 'chart'; imgToggle.dataset.cover = coverUrl; imgToggle.dataset.chart = chartUrl;
    } else {
      imgToggle.style.display = 'none';
    }

    $modal.querySelector('.sc-modal-series-bar').style.background = SC_COLOR;
    var tag = $modal.querySelector('.sc-modal-series-tag');
    tag.textContent = 'Admiralty Small Craft Charts';
    tag.style.color = SC_COLOR;

    $modal.querySelector('.sc-modal-chart-ref').textContent = '' + entry.chartRef;
    $modal.querySelector('.sc-modal-short-title').textContent = entry.shortTitle;

    var specs = [];
    if (entry.scale) specs.push(['Scale', entry.scale]);
    if (entry.correctedTo) specs.push(['Corrected to', entry.correctedTo]);
    if (entry.seaAreas && entry.seaAreas.length) specs.push(['Sea area', entry.seaAreas.join('; ')]);
    if (entry.region) specs.push(['Region', entry.region]);
    $modal.querySelector('.sc-modal-specs').innerHTML = specs.map(function(s){
      return '<div class="sc-modal-spec-row"><span class="sc-modal-spec-label">' + esc(s[0]) + '</span><span class="sc-modal-spec-value">' + esc(s[1]) + '</span></div>';
    }).join('');

    // Panels (multi-panel charts)
    var plansHtml = '';
    var plans = entry.plans || [];
    if (plans.length) {
      plansHtml = '<div class="sc-modal-plans"><div class="sc-modal-plans-title">Panels</div>';
      plans.forEach(function(p) {
        var label = (p.letter ? p.letter + '. ' : '') + (p.title || '');
        plansHtml += '<div class="sc-modal-plan-row"><span class="sc-modal-plan-name">' + esc(label) + '</span>' +
          (p.scale ? '<span class="sc-modal-plan-scale">' + esc(p.scale) + '</span>' : '') + '</div>';
      });
      plansHtml += '</div>';
    }
    // Insert plans after specs
    var specsEl = $modal.querySelector('.sc-modal-specs');
    var existingPlans = $modal.querySelector('.sc-modal-plans');
    if (existingPlans) existingPlans.remove();
    if (plansHtml) specsEl.insertAdjacentHTML('afterend', plansHtml);

    $modal.querySelector('.sc-modal-types').innerHTML = (entry.types || []).map(function(t){
      return '<span class="sc-type-badge">' + esc(t) + '</span>';
    }).join('');

    var folio = folioEntries.find(function(f){ return f.folio === entry.folio; });
    var folioChartCount = folio ? folio.chartCount : '';
    var rowsEl = $modal.querySelector('.sc-modal-product-rows');
    rowsEl.innerHTML = prods.map(function(p) {
      var price = p.price ? fmtPrice(p.price) : '\u2014';
      var vid = p.variantId || 0, inCart = cartVariantIds.has(vid);
      var label = p.isFolioSet ? 'All ' + folioChartCount + ' charts in this folio' : 'This chart only';
      return '<div class="sc-modal-product-row">' +
        '<span class="sc-modal-product-format">' + esc(label) + '</span>' +
        '<span class="sc-modal-product-price">' + price + '</span>' +
        '<button class="sc-btn-cart' + (inCart ? ' in-cart' : '') + '" data-variant-id="' + vid + '" data-entry-id="' + esc(entry.id) + '">' +
        (inCart ? 'In cart \u2713' : 'Add to cart') + '</button></div>';
    }).join('');

    rowsEl.querySelectorAll('.sc-btn-cart').forEach(function(btn) {
      var vid = parseInt(btn.dataset.variantId, 10), eid = btn.dataset.entryId;
      btn.addEventListener('click', function(){
        if (btn.classList.contains('in-cart')) removeFromCart(vid, eid, btn, 'modal');
        else addToCart(vid, eid, btn, 'modal');
      });
    });

    var discountNote = $modal.querySelector('.sc-modal-discount-note');
    if (discountNote) discountNote.textContent = '';

    var viewLink = $modal.querySelector('.sc-modal-view-link');
    if (viewLink) viewLink.href = firstP.handle ? '/products/' + firstP.handle : '#';

    var body = $modal.querySelector('.sc-modal-body');
    if (body) body.scrollTop = 0;

    $modal.classList.add('open');
    document.body.style.overflow = 'hidden';
    var closeBtn = $modal.querySelector('.sc-modal-close');
    if (closeBtn) setTimeout(function(){ closeBtn.focus(); }, 100);
  }

  function closeModal() {
    if ($modal) $modal.classList.remove('open');
    document.body.style.overflow = '';
  }

  /* ================================================================
     CART
     ================================================================ */
  function addToCart(variantId, entryId, btn, source) {
    btn.disabled = true; btn.textContent = 'Adding\u2026';
    fetch('/cart/add.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    })
    .then(function(r){ if (!r.ok) return r.json().then(function(d){ throw new Error(d.description || 'Error'); }); return r.json(); })
    .then(function() {
      cartVariantIds.add(variantId);
      markInCart(variantId, entryId);
      var entry = entriesById.get(entryId);
      if (entry) updatePolyCartHighlight(entry);
      refreshCartBubble(); refreshCartDrawer();
      showToast((entry ? '' + entry.chartRef : 'Chart') + ' added to cart');
    })
    .catch(function(err){ btn.disabled = false; btn.textContent = 'Add to cart'; showToast(err.message || 'Could not add to cart'); });
  }

  function removeFromCart(variantId, entryId, btn, source) {
    btn.disabled = true; btn.textContent = 'Removing\u2026';
    var updates = {};
    updates[String(variantId)] = 0;
    fetch('/cart/update.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ updates: updates })
    })
    .then(function(r){ if (!r.ok) return r.json().then(function(d){ throw new Error(d.description || 'Error'); }); return r.json(); })
    .then(function(cart) {
      cartVariantIds.clear();
      (cart.items || []).forEach(function(i){ cartVariantIds.add(i.variant_id); });
      markNotInCart(variantId, entryId);
      chartEntries.forEach(function(e){ updatePolyCartHighlight(e); });
      refreshCartBubble(); refreshCartDrawer();
      var entry = entriesById.get(entryId);
      showToast((entry ? '' + entry.chartRef : 'Chart') + ' removed from cart');
    })
    .catch(function(err){ btn.disabled = false; btn.textContent = 'In cart \u2713'; showToast(err.message || 'Could not remove'); });
  }

  function markInCart(variantId, entryId) {
    if ($modal) {
      $modal.querySelectorAll('.sc-btn-cart[data-variant-id="' + variantId + '"]').forEach(function(b){
        b.textContent = 'In cart \u2713'; b.classList.add('in-cart'); b.disabled = false;
        b.onclick = function(){ removeFromCart(variantId, entryId, b, 'modal'); };
      });
    }
    if ($bar) {
      var barBtn = $bar.querySelector('.sc-bar-btn-cart');
      if (barBtn && parseInt(barBtn.dataset.variantId, 10) === variantId) {
        barBtn.textContent = 'In cart \u2713'; barBtn.classList.add('in-cart'); barBtn.disabled = false;
        barBtn.onclick = function(){ removeFromCart(variantId, entryId, barBtn, 'bar'); };
      }
    }
  }

  function markNotInCart(variantId, entryId) {
    if ($modal) {
      $modal.querySelectorAll('.sc-btn-cart[data-variant-id="' + variantId + '"]').forEach(function(b){
        b.textContent = 'Add to cart'; b.classList.remove('in-cart'); b.disabled = false;
        b.onclick = function(){ addToCart(variantId, entryId, b, 'modal'); };
      });
    }
    if ($bar) {
      var barBtn = $bar.querySelector('.sc-bar-btn-cart');
      if (barBtn && parseInt(barBtn.dataset.variantId, 10) === variantId) {
        barBtn.textContent = 'Add to cart'; barBtn.classList.remove('in-cart'); barBtn.disabled = false;
        barBtn.onclick = function(){ addToCart(variantId, entryId, barBtn, 'bar'); };
      }
    }
  }

  function refreshCartBubble() { fetch('/?sections=cart-icon-bubble').then(function(r){ return r.json(); }).then(function(s){ var h = s['cart-icon-bubble']; if (h) { var el = document.getElementById('cart-icon-bubble'); if (el) el.innerHTML = h; } }).catch(function(){}); }
  function refreshCartDrawer() { document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh')); }
  function fetchCartState() { fetch('/cart.json').then(function(r){ return r.json(); }).then(function(cart){ (cart.items || []).forEach(function(i){ cartVariantIds.add(i.variant_id); }); chartEntries.forEach(function(e){ updatePolyCartHighlight(e); }); if (activeEntry && $bar && $bar.classList.contains('open')) showBar(activeEntry); }).catch(function(){}); }

  function updatePolyCartHighlight(entry) {
    var poly = chartPolygons.get(entry.id); if (!poly) return;
    var inCart = (entry.products || []).some(function(p){ return cartVariantIds.has(p.variantId); });
    var el = poly.getElement ? poly.getElement() : null;
    if (el) el.classList.toggle('sc-poly-in-cart', inCart);
  }

  /* ================================================================
     SEARCH — tokenised metadata + Nominatim geocoding
     ================================================================ */
  var currentChartResults = [], currentPlaceResults = [], focusIdx = -1, nominatimTimer, nominatimAbort;

  function buildSearchIndex() {
    searchIndex = [];
    chartEntries.forEach(function(e) {
      var text = [e.chartRef, e.number, e.shortTitle, e.title, (e.seaAreas || []).join(' '), e.zone || '', e.region || '', (e.harbours || []).join(' ')].join(' ').toLowerCase();
      searchIndex.push({ entry: e, text: text });
    });
    folioEntries.forEach(function(f) {
      var text = [f.folio, f.title || '', (f.seaAreas || []).join(' '), (f.regions || []).join(' ')].join(' ').toLowerCase();
      searchIndex.push({ folio: f, text: text });
    });
  }

  function searchIdx(q) {
    var tok = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!tok.length) return [];
    return searchIndex.filter(function(i) { return tok.every(function(t) { return i.text.indexOf(t) !== -1; }); }).slice(0, 10);
  }

  function searchNominatim(q) {
    if (nominatimAbort) nominatimAbort.abort();
    nominatimAbort = new AbortController();
    return fetch('https://nominatim.openstreetmap.org/search?format=json&limit=4&q=' + encodeURIComponent(q) + '&accept-language=en', { signal: nominatimAbort.signal, headers: { Accept: 'application/json' } })
      .then(function(r) { return r.ok ? r.json() : []; })
      .catch(function(e) { return e.name === 'AbortError' ? null : []; });
  }

  function initSearch() {
    if (!$searchInput) return;
    $searchInput.addEventListener('input', function() {
      var q = $searchInput.value.trim();
      $searchClear.style.display = q ? 'block' : 'none';
      if (!q) { hideDropdown(); currentChartResults = []; currentPlaceResults = []; return; }
      currentChartResults = searchIdx(q);
      currentPlaceResults = [];
      if (currentChartResults.length || q.length >= 2) renderDropdown();
      clearTimeout(nominatimTimer);
      if (q.length >= 3) nominatimTimer = setTimeout(function() {
        searchNominatim(q).then(function(p) { if (p === null) return; currentPlaceResults = p || []; renderDropdown(); });
      }, 500);
    });
    $searchInput.addEventListener('keydown', function(e) {
      var items = $searchDropdown.querySelectorAll('.sc-search-result');
      if (!items.length && e.key !== 'Escape') return;
      if (e.key === 'ArrowDown') { e.preventDefault(); focusIdx = Math.min(focusIdx + 1, items.length - 1); items.forEach(function(el, i) { el.classList.toggle('focused', i === focusIdx); }); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); focusIdx = Math.max(focusIdx - 1, -1); items.forEach(function(el, i) { el.classList.toggle('focused', i === focusIdx); }); }
      else if (e.key === 'Enter' && focusIdx >= 0) { e.preventDefault(); items[focusIdx].click(); }
      else if (e.key === 'Escape') { hideDropdown(); $searchInput.blur(); }
    });
    $searchInput.addEventListener('focus', function() { if ($searchInput.value.trim() && (currentChartResults.length || currentPlaceResults.length)) renderDropdown(); });
    $searchClear.addEventListener('click', function() { $searchInput.value = ''; $searchClear.style.display = 'none'; hideDropdown(); currentChartResults = []; currentPlaceResults = []; $searchInput.focus(); });
    document.addEventListener('click', function(e) { if (!$searchInput.contains(e.target) && !$searchDropdown.contains(e.target)) hideDropdown(); });
  }

  function renderDropdown() {
    var hc = currentChartResults.length > 0, hp = currentPlaceResults.length > 0;
    if (!hc && !hp) {
      $searchDropdown.innerHTML = '<div class="sc-search-no-results">No results</div>';
      $searchDropdown.style.display = 'block'; focusIdx = -1; return;
    }
    var html = '';
    if (hc) {
      html += '<div class="sc-search-section-label">Charts</div>';
      currentChartResults.forEach(function(item, i) {
        if (item.folio && !item.entry) {
          var f = item.folio;
          html += '<div class="sc-search-result" data-kind="folio" data-idx="' + i + '"><div class="sc-sr-ref" style="color:' + SC_COLOR_FOLIO + '">Folio ' + esc(f.folio) + '</div><div class="sc-sr-title">' + f.chartCount + ' charts' + (f.seaAreas ? ' \u00b7 ' + (f.seaAreas || []).slice(0, 3).join(', ') : '') + '</div></div>';
        } else {
          var e = item.entry;
          html += '<div class="sc-search-result" data-kind="chart" data-idx="' + i + '"><div class="sc-sr-ref" style="color:' + SC_COLOR + '">' + esc(e.chartRef) + '</div><div class="sc-sr-title">' + esc(e.shortTitle) + '</div></div>';
        }
      });
    }
    if (hp) {
      html += '<div class="sc-search-section-label">Places</div>';
      currentPlaceResults.forEach(function(p, i) {
        html += '<div class="sc-search-result" data-kind="place" data-idx="' + i + '"><div class="sc-sr-ref">' + esc(p.display_name.split(',').slice(0, 3).join(',')) + '</div></div>';
      });
    }
    $searchDropdown.innerHTML = html;
    $searchDropdown.style.display = 'block'; focusIdx = -1;
    $searchDropdown.querySelectorAll('[data-kind="chart"]').forEach(function(el) {
      var idx = +el.dataset.idx;
      el.addEventListener('click', function() { selectChartResult(currentChartResults[idx].entry); });
    });
    $searchDropdown.querySelectorAll('[data-kind="folio"]').forEach(function(el) {
      var idx = +el.dataset.idx;
      el.addEventListener('click', function() { selectFolioResult(currentChartResults[idx].folio); });
    });
    $searchDropdown.querySelectorAll('[data-kind="place"]').forEach(function(el) {
      var idx = +el.dataset.idx;
      el.addEventListener('click', function() { selectPlaceResult(currentPlaceResults[idx]); });
    });
  }

  function hideDropdown() { if ($searchDropdown) $searchDropdown.style.display = 'none'; focusIdx = -1; }

  function selectChartResult(entry) {
    hideDropdown();
    $searchInput.value = entry.chartRef; $searchClear.style.display = 'block';
    // Ensure we're in a view where the chart is visible
    if (viewMode === 'folios' && !currentFolio) {
      // Switch to individual charts mode
      viewMode = 'charts';
      if ($modeCharts) $modeCharts.classList.add('active');
      if ($modeFolios) $modeFolios.classList.remove('active');
      drawAllCharts();
    } else if (viewMode === 'folios' && currentFolio && currentFolio !== entry.folio) {
      drillIntoFolio(entry.folio);
    } else if (viewMode === 'charts') {
      // Already in all-charts mode — polygon should exist
    }
    setTimeout(function() {
      var poly = chartPolygons.get(entry.id);
      if (entry && poly) {
        selectChart(entry, poly);
        map.fitBounds(poly.getBounds(), { padding: [60, 60], maxZoom: 12 });
      } else if (entry.coordinates && entry.coordinates.length) {
        try { map.fitBounds(L.polygon(entry.coordinates).getBounds(), { padding: [60, 60] }); } catch (e) {}
      }
    }, 50);
  }

  function selectFolioResult(folio) {
    hideDropdown();
    $searchInput.value = 'Folio ' + folio.folio; $searchClear.style.display = 'block';
    // Switch to folios mode and drill in
    viewMode = 'folios';
    if ($modeCharts) $modeCharts.classList.remove('active');
    if ($modeFolios) $modeFolios.classList.add('active');
    drillIntoFolio(folio.folio);
  }

  function selectPlaceResult(place) {
    hideDropdown();
    $searchInput.value = place.display_name.split(',').slice(0, 2).join(',').trim();
    $searchClear.style.display = 'block';
    // Always switch to individual charts view and clear selection
    activeFolio = null;
    currentFolio = null;
    if (viewMode !== 'charts') {
      viewMode = 'charts';
      if ($modeCharts) $modeCharts.classList.add('active');
      if ($modeFolios) $modeFolios.classList.remove('active');
      drawAllCharts();
    }
    closeBar();
    updateNavUI();
    var lat = parseFloat(place.lat), lon = parseFloat(place.lon);
    if (isNaN(lat) || isNaN(lon)) return;
    var zoomMap = { country: 5, state: 6, region: 6, county: 7, city: 8, town: 9, village: 9, bay: 9, sea: 7, harbour: 11, marina: 12, island: 9 };
    var mz = zoomMap[place.type] || 9;
    if (place.boundingbox && place.boundingbox.length === 4) {
      var bb = place.boundingbox.map(Number);
      map.fitBounds([[bb[0], bb[2]], [bb[1], bb[3]]], { padding: [60, 60], maxZoom: mz });
    } else {
      map.setView([lat, lon], mz);
    }
  }

  /* ── Tooltip ── */
  function showFolioTooltip(e, folio) {
    if (isMobile || !$tooltip) return;
    $tooltip.innerHTML = '<div class="sc-tt-ref">' + esc(folio.folio) + ': ' + esc(folio.title || '') + '</div>' +
      '<div class="sc-tt-detail">' + folio.chartCount + ' charts</div>' +
      '<div class="sc-tt-detail" style="margin-top:4px;color:' + SC_COLOR + ';font-weight:600">Click to explore &rarr;</div>';
    $tooltip.style.display = 'block'; moveTooltip(e);
  }

  function showChartTooltip(e, entry) {
    if (isMobile || !$tooltip) return;
    var price = (entry.products && entry.products[0]) ? fmtPrice(entry.products[0].price) : '';
    var plansText = '';
    var plans = entry.plans || [];
    if (plans.length) {
      plansText = '<div class="sc-tt-detail" style="margin-top:3px">' + plans.length + ' panels: ' +
        plans.map(function(p) { return (p.letter ? p.letter : '') + (p.title ? ' ' + p.title : ''); }).join(', ') + '</div>';
    }
    $tooltip.innerHTML = '<div class="sc-tt-ref">' + esc(entry.chartRef) + '</div>' +
      '<div class="sc-tt-title">' + esc(entry.shortTitle) + '</div>' +
      (entry.scale ? '<div class="sc-tt-detail">Scale ' + esc(entry.scale) + '</div>' : '') +
      plansText +
      (price ? '<div class="sc-tt-detail">' + price + '</div>' : '');
    $tooltip.style.display = 'block'; moveTooltip(e);
  }

  function moveTooltip(e) {
    var off = 14, l = e.clientX + off, t = e.clientY + off;
    if (l + $tooltip.offsetWidth > window.innerWidth - 8) l = e.clientX - $tooltip.offsetWidth - off;
    if (t + $tooltip.offsetHeight > window.innerHeight - 8) t = e.clientY - $tooltip.offsetHeight - off;
    $tooltip.style.left = l + 'px'; $tooltip.style.top = t + 'px';
  }
  function hideTooltip() { if ($tooltip) $tooltip.style.display = 'none'; }

  var toastTimer;
  function showToast(msg) { if (!$toast) return; $toast.textContent = msg; $toast.classList.add('visible'); clearTimeout(toastTimer); toastTimer = setTimeout(function(){ $toast.classList.remove('visible'); }, 3000); }

  function esc(s) { if (!s) return ''; var d = document.createElement('div'); d.textContent = s; return d.innerHTML; }

})();
